import {
  InputAreaUniDriver,
  InputUniDriver,
  RadioGroupUniDriver,
  TextButtonUniDriver,
} from '@wix/design-system/dist/testkit/unidriver';
import type { GetOptions, Testkit, UniDriver } from 'ricos-types/testkit';
import { dataHook, RicosPluginToolbarNames } from 'wix-rich-content-common';
import { INSERT_PLUGIN_BUTTONS } from 'wix-rich-content-editor-common';

import {
  EDIT_PANEL,
  EDIT_PANEL_CANCEL_BUTTON,
  EDIT_PANEL_HTML_INPUT,
  EDIT_PANEL_RADIO_GROUP,
  EDIT_PANEL_UPDATE_BUTTON,
  EDIT_PANEL_URL_INPUT,
  IFRAME_COMPONENT,
  ROOT,
  TOOLBAR_BUTTON_EDIT_HTML,
} from './dataHooks';
import { toolbarsToAddIn } from './getAddButtons';

export type HtmlTestkit = Testkit<{
  content: {
    atIndex: (index: number) => {
      exists: (options?: GetOptions) => Promise<boolean>;
      getSrc: () => Promise<string | null>;
      getIframeNativeElement: () => Promise<Element>;
    };
  };
  editHtmlModal: {
    options: ReturnType<typeof RadioGroupUniDriver>;
    htmlInputArea: ReturnType<typeof InputAreaUniDriver>;
    urlInput: ReturnType<typeof InputUniDriver>;
    saveButton: ReturnType<typeof TextButtonUniDriver>;
    cancelButton: ReturnType<typeof TextButtonUniDriver>;
    waitForElement: () => Promise<void>;
  };
  toolbars: {
    createAddButtonToolbars: (getBase: () => UniDriver<Element>) => {
      clickHtml: () => Promise<void>;
      clickAdsense: () => Promise<void>;
      toolbarsToAddIn: typeof toolbarsToAddIn;
    };
    createPluginToolbar: (
      getBase: (pluginType: typeof RicosPluginToolbarNames.HTML) => UniDriver<Element>
    ) => {
      exists: () => Promise<boolean>;
      clickEditHtml: () => Promise<void>;
      clickDelete: () => Promise<void>;
    };
  };
}>;

export const createHtmlTestkit: HtmlTestkit = getBase => {
  const getHtml = async (index: number, options?: GetOptions) => {
    const html = await getBase().getAll(dataHook(ROOT), options);
    return html.at(index);
  };
  const getIframe = async (index: number) => {
    const iframe = await getBase().getAll(dataHook(IFRAME_COMPONENT));
    return iframe.at(index);
  };

  return {
    content: {
      atIndex: index => {
        return {
          exists: async options => {
            try {
              const html = await getHtml(index, options);
              return html.exists();
            } catch (error) {
              return false;
            }
          },
          getSrc: async () => (await getIframe(index)).attr('src'),
          getIframeNativeElement: async () => {
            const iframe = await getIframe(index);
            return iframe.getNative();
          },
        };
      },
    },
    editHtmlModal: {
      options: RadioGroupUniDriver(getBase().$(dataHook(EDIT_PANEL_RADIO_GROUP)), getBase()),
      htmlInputArea: InputAreaUniDriver(getBase().$(dataHook(EDIT_PANEL_HTML_INPUT)), getBase()),
      urlInput: InputUniDriver(getBase().$(dataHook(EDIT_PANEL_URL_INPUT)), getBase()),
      saveButton: TextButtonUniDriver(getBase().$(dataHook(EDIT_PANEL_UPDATE_BUTTON)), getBase()),
      cancelButton: TextButtonUniDriver(getBase().$(dataHook(EDIT_PANEL_CANCEL_BUTTON)), getBase()),
      waitForElement: async () => {
        const el = await getBase().get(dataHook(EDIT_PANEL));
        await el.wait();
      },
    },
    toolbars: {
      createAddButtonToolbars: getAddButtonBase => ({
        clickHtml: async () => {
          const htmlButton = await getAddButtonBase().get(dataHook(INSERT_PLUGIN_BUTTONS.HTML));
          await htmlButton.click();
        },
        clickAdsense: async () => {
          const adsenseButton = await getAddButtonBase().get(
            dataHook(INSERT_PLUGIN_BUTTONS.ADSENSE)
          );
          await adsenseButton.click();
        },
        toolbarsToAddIn,
      }),
      createPluginToolbar: getPluginToolbarBase => {
        return {
          exists: async () => {
            try {
              return getPluginToolbarBase(RicosPluginToolbarNames.HTML).exists();
            } catch (error) {
              return false;
            }
          },
          clickEditHtml: async () => {
            const editHTMLButton = await getPluginToolbarBase(RicosPluginToolbarNames.HTML).get(
              dataHook(TOOLBAR_BUTTON_EDIT_HTML)
            );
            await editHTMLButton.click();
          },
          clickDelete: async () => {
            const deleteButton = await getPluginToolbarBase(RicosPluginToolbarNames.HTML).get(
              dataHook('blockButton_delete')
            );
            await deleteButton.click();
          },
        };
      },
    },
  };
};
